import React, { useState, useEffect } from 'react';
import { cn } from '@/lib/utils';
import CircularProgress from '../ui/CircularProgress';
import { DropletIcon } from 'lucide-react';

interface WaterUsageCardProps {
  title: string;
  value: number;
  unit: string;
  percentage: number;
  trend: 'up' | 'down' | 'neutral';
  trendValue: number;
  className?: string;
}

const WaterUsageCard = ({
  title,
  value,
  unit,
  percentage,
  trend,
  trendValue,
  className
}: WaterUsageCardProps) => {
  const [isVisible, setIsVisible] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  
  useEffect(() => {
    const timer = setTimeout(() => {
      setIsVisible(true);
    }, 100);
    
    return () => clearTimeout(timer);
  }, []);
  
  const trendColor = 
    trend === 'down' 
      ? 'text-nature-500' 
      : trend === 'up' 
        ? 'text-destructive' 
        : 'text-muted-foreground';
  
  const trendIcon = 
    trend === 'down' 
      ? '↓' 
      : trend === 'up' 
        ? '↑' 
        : '→';
  
  return (
    <div
      className={cn(
        'bg-card shadow-soft-lg rounded-lg p-6 transition-all duration-500 transform',
        isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4',
        isHovered && 'scale-[1.02]',
        className
      )}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <div className="flex items-start justify-between">
        <div>
          <h3 className="font-medium text-muted-foreground">{title}</h3>
          <div className="mt-2 flex items-baseline">
            <p className="text-3xl font-bold">{value}</p>
            <span className="ml-1 text-muted-foreground">{unit}</span>
          </div>
          <div className={cn("flex items-center mt-2", trendColor)}>
            <span className="font-medium">
              {trendIcon} {trendValue}%
            </span>
            <span className="ml-1 text-xs text-muted-foreground">vs. last month</span>
          </div>
        </div>
        
        <CircularProgress 
          value={percentage} 
          size={80} 
          color={
            percentage < 30 
              ? "stroke-nature-500" 
              : percentage < 70 
                ? "stroke-efficiency-500" 
                : "stroke-destructive"
          }
        >
          <div className="flex flex-col items-center justify-center">
            <DropletIcon 
              size={20} 
              className={cn(
                percentage < 30 
                  ? "text-nature-500" 
                  : percentage < 70 
                    ? "text-efficiency-500" 
                    : "text-destructive"
              )} 
            />
            <span className="text-xs font-medium mt-1">{percentage}%</span>
          </div>
        </CircularProgress>
      </div>
      
      <div className="mt-4 h-2 bg-muted rounded-full overflow-hidden">
        <div 
          className={cn(
            "h-full rounded-full transition-all duration-1000 ease-out",
            percentage < 30 
              ? "bg-nature-500" 
              : percentage < 70 
                ? "bg-efficiency-500" 
                : "bg-destructive"
          )} 
          style={{ width: `${isVisible ? percentage : 0}%` }}
        ></div>
      </div>
    </div>
  );
};

export default WaterUsageCard;
